import { createSlice, createAsyncThunk, isAnyOf } from '@reduxjs/toolkit';
import api from '../api/axios';
import { login, logout, getMe, mfaVerified, ssoConsumeToken } from './authSlice';

const BASE = '/api/v1/cloud';

export const scopeOf = (p) => [p?.cloud_provider || 'all', p?.cloud_account_id || 'all', p?.days ?? ''].join('|');

// ── Dashboard ──
export const fetchDashboard = createAsyncThunk(
  'cloud/fetchDashboard',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/dashboard/summary`, { params });
      if (data?.summary) return data;
      return rejectWithValue('No dashboard data returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load dashboard');
    }
  }
);

export const fetchSecurityScore = createAsyncThunk(
  'cloud/fetchSecurityScore',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/dashboard/security-score`, { params });
      return { ...data, score: data?.score ?? data?.securityScore ?? null };
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load security score');
    }
  }
);

export const fetchTrends = createAsyncThunk(
  'cloud/fetchTrends',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/dashboard/trends`, { params });
      return data?.trends || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load findings trend');
    }
  }
);

export const fetchTopRisks = createAsyncThunk(
  'cloud/fetchTopRisks',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/dashboard/top-risks`, { params });
      return data?.risks || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load top risks');
    }
  }
);

// ── Accounts ──
export const fetchAccounts = createAsyncThunk(
  'cloud/fetchAccounts',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/accounts`);
      if (data?.accounts) return data;
      return rejectWithValue('No accounts data returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load accounts');
    }
  }
);

// Lightweight account list for the global selector (no heavy joins/stats)
export const fetchAccountsLookup = createAsyncThunk(
  'cloud/fetchAccountsLookup',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/accounts/lookup`, { params });
      return data?.accounts || [];
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load accounts lookup');
    }
  }
);

export const addAccount = createAsyncThunk('cloud/addAccount', async (payload, { rejectWithValue }) => {
  try {
    const { data } = await api.post(`${BASE}/accounts`, payload);
    return data;
  } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed'); }
});

export const deleteAccount = createAsyncThunk('cloud/deleteAccount', async (id, { rejectWithValue }) => {
  try {
    await api.delete(`${BASE}/accounts/${encodeURIComponent(id)}`);
    return id;
  } catch (err) { return rejectWithValue(err.response?.data?.error || 'Failed'); }
});

// ── Cloud account setup wizard ──
export const generateSetupTemplate = createAsyncThunk(
  'cloud/generateSetupTemplate',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${BASE}/accounts/setup/template`, payload);
      return data.template;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to generate setup template');
    }
  }
);

export const validateSetupPermissions = createAsyncThunk(
  'cloud/validateSetupPermissions',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${BASE}/accounts/setup/validate-permissions`, payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Permission validation failed');
    }
  }
);

export const finalizeAccountSetup = createAsyncThunk(
  'cloud/finalizeAccountSetup',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${BASE}/accounts/setup/finalize`, payload);
      return data.account;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to finalize account');
    }
  }
);

export const rotateAccountCredentials = createAsyncThunk(
  'cloud/rotateAccountCredentials',
  async ({ id, credentials }, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${BASE}/accounts/${encodeURIComponent(id)}/rotate-credentials`, { credentials });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to rotate credentials');
    }
  }
);

export const fetchAccountAuditLog = createAsyncThunk(
  'cloud/fetchAccountAuditLog',
  async ({ id, limit = 100 } = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/accounts/${encodeURIComponent(id)}/audit-log`, { params: { limit } });
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load audit log');
    }
  }
);

export const fetchAvailableFrameworks = createAsyncThunk(
  'cloud/fetchAvailableFrameworks',
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/accounts/compliance/frameworks/available`);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load frameworks');
    }
  }
);

export const fetchAccountById = createAsyncThunk(
  'cloud/fetchAccountById',
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/accounts/${encodeURIComponent(id)}`);
      return data.account;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load account');
    }
  }
);

// ── Assets ──
export const fetchAssets = createAsyncThunk(
  'cloud/fetchAssets',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/assets`, { params });
      if (data?.assets) return data;
      return rejectWithValue('No assets data returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load assets');
    }
  }
);

export const fetchAssetStats = createAsyncThunk(
  'cloud/fetchAssetStats',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/assets/stats`, { params });
      if (data?.stats) return data;
      return rejectWithValue('No asset stats returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load asset stats');
    }
  }
);

// ── Findings ──
export const fetchFindings = createAsyncThunk(
  'cloud/fetchFindings',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/findings`, { params });
      if (data?.findings) return data;
      return rejectWithValue('No findings data returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load findings');
    }
  }
);

export const fetchFindingsStats = createAsyncThunk(
  'cloud/fetchFindingsStats',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/findings/stats`, { params });
      if (data?.stats) return data;
      return rejectWithValue('No findings stats returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load findings stats');
    }
  }
);

// ── IAM ──
export const fetchIamEntities = createAsyncThunk(
  'cloud/fetchIamEntities',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/iam`, { params });
      if (data?.entities) return data;
      return rejectWithValue('No IAM entities data returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load IAM entities');
    }
  }
);

export const fetchIamRiskSummary = createAsyncThunk(
  'cloud/fetchIamRiskSummary',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/iam/risk-summary`, { params });
      if (data?.summary) return data;
      return rejectWithValue('No IAM risk summary returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load IAM risk summary');
    }
  }
);

// ── Compliance ──
export const fetchComplianceSummary = createAsyncThunk(
  'cloud/fetchComplianceSummary',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/compliance`, { params });
      if (data?.summary) return data;
      return rejectWithValue('No compliance data returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load compliance');
    }
  }
);

// ── Network ──
export const fetchNetworkOverview = createAsyncThunk(
  'cloud/fetchNetworkOverview',
  async (params = {}, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/network`, { params });
      if (data?.overview) return data;
      return rejectWithValue('No network data returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load network overview');
    }
  }
);

// ── Scans ──
export const triggerScan = createAsyncThunk(
  'cloud/triggerScan',
  async (payload, { rejectWithValue }) => {
    try {
      const { data } = await api.post(`${BASE}/scans`, payload);
      return data;
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to trigger scan');
    }
  }
);

export const fetchScans = createAsyncThunk(
  'cloud/fetchScans',
  async (params, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`${BASE}/scans`, { params });
      if (data?.scans) return data;
      return rejectWithValue('No scans data returned');
    } catch (err) {
      return rejectWithValue(err.response?.data?.error || 'Failed to load scans');
    }
  }
);

const initialState = () => ({
  refreshTick: 0,
  selectedProvider: 'all',
  selectedAccountId: null,
  accountsLookup: [],
  dashboard: null,
  securityScore: null,
  trends: [],
  topRisks: [],
  accounts: [],
  accountsSummary: null,
  assets: [], assetsPagination: null, assetStats: null,
  findings: [], findingsPagination: null, findingsStats: null,
  iamEntities: [], iamRiskSummary: null,
  complianceSummary: [],
  networkOverview: null,
  scans: [], activeScan: null,
  setupTemplate: null,
  setupValidation: null,
  availableFrameworks: null,
  currentAccount: null,
  accountAuditLog: [],
  loading: {},
  errors: {},
  // Scope (provider|account|days) of the data each key holds, so another scope's data is never shown.
  loaded: {},
  requests: {},
  error: null
});

const TRACKED = [
  ['dashboard', fetchDashboard, (s, p) => { s.dashboard = p; }, ['dashboard']],
  ['securityScore', fetchSecurityScore, (s, p) => { s.securityScore = p; }, ['securityScore']],
  ['trends', fetchTrends, (s, p) => { s.trends = p; }, ['trends']],
  ['topRisks', fetchTopRisks, (s, p) => { s.topRisks = p; }, ['topRisks']],
  ['accounts', fetchAccounts, (s, p) => { s.accounts = p.accounts || []; }, ['accounts']],
  ['assets', fetchAssets, (s, p) => { s.assets = p.assets || []; s.assetsPagination = p.pagination; }, ['assets', 'assetsPagination']],
  ['assetStats', fetchAssetStats, (s, p) => { s.assetStats = p.stats; }, ['assetStats']],
  ['findings', fetchFindings, (s, p) => { s.findings = p.findings || []; s.findingsPagination = p.pagination; }, ['findings', 'findingsPagination']],
  ['findingsStats', fetchFindingsStats, (s, p) => { s.findingsStats = p.stats; }, ['findingsStats']],
  ['iam', fetchIamEntities, (s, p) => { s.iamEntities = p.entities || []; }, ['iamEntities']],
  ['iamRiskSummary', fetchIamRiskSummary, (s, p) => { s.iamRiskSummary = p.summary; }, ['iamRiskSummary']],
  ['compliance', fetchComplianceSummary, (s, p) => { s.complianceSummary = p.summary || []; }, ['complianceSummary']],
  ['network', fetchNetworkOverview, (s, p) => { s.networkOverview = p.overview; }, ['networkOverview']],
  ['scans', fetchScans, (s, p) => { s.scans = p.scans || []; }, ['scans']]
];

const cloudSlice = createSlice({
  name: 'cloud',
  initialState: initialState(),
  reducers: {
    clearCloudError: (state) => { state.error = null; },
    requestRefresh: (state) => { state.refreshTick += 1; },
    setSelectedProvider: (state, action) => {
      state.selectedProvider = action.payload;
      // The selected account may not exist under the new provider scope.
      state.selectedAccountId = null;
    },
    setSelectedAccount: (state, action) => {
      state.selectedAccountId = action.payload || null;
    },
    resetSetupWizard: (state) => {
      state.setupTemplate = null;
      state.setupValidation = null;
    }
  },
  extraReducers: (builder) => {
    const setLoading = (key) => (state) => { state.loading[key] = true; };

    TRACKED.forEach(([key, thunk, apply, fields]) => {
      builder
        .addCase(thunk.pending, (state, a) => {
          const scope = scopeOf(a.meta.arg);
          if (state.loaded[key] !== undefined && state.loaded[key] !== scope) {
            const fresh = initialState();
            fields.forEach((f) => { state[f] = fresh[f]; });
            delete state.loaded[key];
          }
          state.requests[key] = a.meta.requestId;
          state.loading[key] = true;
          state.errors[key] = null;
        })
        .addCase(thunk.fulfilled, (state, a) => {
          if (state.requests[key] !== a.meta.requestId) return;
          apply(state, a.payload);
          state.loaded[key] = scopeOf(a.meta.arg);
          state.loading[key] = false;
          state.error = null;
        })
        .addCase(thunk.rejected, (state, a) => {
          if (state.requests[key] !== a.meta.requestId) return;
          state.loading[key] = false;
          state.errors[key] = a.payload || a.error?.message || 'Request failed';
          state.error = state.errors[key];
        });
    });

    builder
      .addCase(fetchAccountsLookup.fulfilled, (state, a) => {
        state.accountsLookup = a.payload || [];
        if (state.selectedAccountId && !state.accountsLookup.some(x => x.id === state.selectedAccountId)) {
          state.selectedAccountId = null;
        }
      })
      .addCase(addAccount.fulfilled, (state, a) => { if (a.payload.account) state.accounts.push(a.payload.account); })
      .addCase(deleteAccount.fulfilled, (state, a) => { state.accounts = state.accounts.filter(acc => acc.id !== a.payload); })
      .addCase(triggerScan.pending, setLoading('triggerScan'))
      .addCase(triggerScan.fulfilled, (state, a) => { state.loading.triggerScan = false; state.activeScan = a.payload; })
      .addCase(triggerScan.rejected, (state) => { state.loading.triggerScan = false; })
      .addCase(generateSetupTemplate.pending, setLoading('setupTemplate'))
      .addCase(generateSetupTemplate.fulfilled, (state, a) => {
        state.loading.setupTemplate = false; state.setupTemplate = a.payload; state.error = null;
      })
      .addCase(generateSetupTemplate.rejected, (state, a) => {
        state.loading.setupTemplate = false; state.error = a.payload;
      })
      .addCase(validateSetupPermissions.pending, setLoading('setupValidation'))
      .addCase(validateSetupPermissions.fulfilled, (state, a) => {
        state.loading.setupValidation = false; state.setupValidation = a.payload; state.error = null;
      })
      .addCase(validateSetupPermissions.rejected, (state, a) => {
        state.loading.setupValidation = false; state.setupValidation = { valid: false, error: a.payload };
      })
      .addCase(finalizeAccountSetup.pending, setLoading('finalizeSetup'))
      .addCase(finalizeAccountSetup.fulfilled, (state, a) => {
        state.loading.finalizeSetup = false;
        if (a.payload) state.accounts.push(a.payload);
        state.error = null;
      })
      .addCase(finalizeAccountSetup.rejected, (state, a) => {
        state.loading.finalizeSetup = false; state.error = a.payload;
      })
      .addCase(fetchAvailableFrameworks.fulfilled, (state, a) => { state.availableFrameworks = a.payload; })
      .addCase(fetchAccountById.pending, setLoading('currentAccount'))
      .addCase(fetchAccountById.fulfilled, (state, a) => {
        state.loading.currentAccount = false; state.currentAccount = a.payload; state.error = null;
      })
      .addCase(fetchAccountById.rejected, (state, a) => {
        state.loading.currentAccount = false; state.error = a.payload;
      })
      .addCase(fetchAccountAuditLog.fulfilled, (state, a) => {
        state.accountAuditLog = a.payload?.events || [];
      })
      // Every identity change (sign-in, org switch, sign-out) drops the previous tenant's cached data.
      .addMatcher(
        isAnyOf(login.fulfilled, logout.pending, logout.fulfilled, getMe.rejected, mfaVerified, ssoConsumeToken),
        () => initialState()
      );
  }
});

export const scopeParams = (provider, accountId, extra = {}) => ({
  ...extra,
  ...(provider && provider !== 'all' ? { cloud_provider: provider } : {}),
  ...(accountId ? { cloud_account_id: accountId } : {})
});

export const selectStatus = (key, params) => (s) => ({
  loading: !!s.cloud.loading[key],
  error: s.cloud.errors?.[key] || null,
  ready: s.cloud.loaded?.[key] === scopeOf(params)
});

export const { clearCloudError, requestRefresh, setSelectedProvider, setSelectedAccount, resetSetupWizard } = cloudSlice.actions;
export default cloudSlice.reducer;
